import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';
import Webcam from 'react-webcam';
import { Box, Typography, LinearProgress } from '@mui/material';
import { toast } from 'react-hot-toast';
import { completeAsana } from 'services/asana/completeAsana';
import useOrientation from './studentFunctionality/useOrientation';
import OrientationWarningOverlay from './studentFunctionality/OrientationWarningOverlay';
import { v4 as uuidv4 } from 'uuid';
import { PutObjectCommand } from '@aws-sdk/client-s3';
import s3awsConfig from 'utils/aws-config';

// Constants
const REQUIRED_CONSECUTIVE_FRAMES = 15;
const HOLD_DURATION_SEC = 12;
const MODEL_PATH =
  'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';
const WASM_PATH = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm';

// Custom hook for pose landmarker initialization
const usePoseLandmarker = (pose) => {
  const [poseLandmarker, setPoseLandmarker] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
  setLoading(true);

  const initModel = async () => {
    const vision = await FilesetResolver.forVisionTasks(WASM_PATH);
    const landmarker = await PoseLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_PATH,
        delegate: 'GPU'
      },
      runningMode: 'VIDEO',
      numPoses: 1
    });
    setPoseLandmarker(landmarker);
    setLoading(false);
  };

  initModel();

  return () => {
    poseLandmarker?.close();
  };
}, []); // 🔥 INIT ONCE ONLY


  return { poseLandmarker, loading };
};

// Custom hook for speech synthesis
const useSpeechSynthesis = () => {
  const lastSpokenTimeRef = useRef(0);
  const lastMessageRef = useRef('');

  const speakCountdown = useCallback((message) => {
    if (message === lastMessageRef.current) return;
    const now = Date.now();
    if (now - lastSpokenTimeRef.current < 3000) return;

    if (typeof window !== 'undefined' && 'speechSynthesis' in window && typeof window.speechSynthesis.cancel === 'function') {
      window.speechSynthesis.cancel();
    }
    const msg = new SpeechSynthesisUtterance(message);
    msg.lang = 'en-US';
    msg.rate = 1.0;

    lastSpokenTimeRef.current = now;
    lastMessageRef.current = message;

    try {
      window.speechSynthesis.speak(msg);
    } catch (error) {
      console.error('Speech error:', error);
    }
  }, []);

  return speakCountdown;
};

const usePoseTracking = (
  isTracking,
  loading,
  poseLandmarker,
  webcamRef,
  canvasRef,
  navigate,
  speakCountdown,
  setIsTracking,
  setSessionData,
  pose,
  onNextPose
) => {
  console.log('usePoseTracking initialized with pose:', pose);
  const [accuracy, setAccuracy] = useState(0);
  const [goodPostureTime, setGoodPostureTime] = useState(0);
  const [latencyMs, setLatencyMs] = useState(0);
  const [fps, setFps] = useState(0);
  const [isCapturing, setIsCapturing] = useState(false);
  const studentId = localStorage.getItem('studentid');
  const queryClient = useQueryClient();

  const animationFrameRef = useRef(null);
  const goodPostureStartRef = useRef(null);
  const lastDistanceStateRef = useRef(null);
  const completedRef = useRef(false);
  const lastVideoTimeRef = useRef(-1);
  const consecutiveFramesRef = useRef(0);
  const lastToastRef = useRef(0);
  const captureStartTimeRef = useRef(null);
  const captureAccuracySamplesRef = useRef([]);
  const captureFrameCountRef = useRef(0);
  const lastCountdownRef = useRef(0);
  const missingLandmarksCounterRef = useRef(0);

  const currentInstructorPose = useMemo(() => {
    if (!pose?.landmarks) return null;
    try {
      return typeof pose.landmarks === 'string' ? JSON.parse(pose.landmarks) : pose.landmarks;
    } catch (e) {
      console.error('Failed to parse instructor landmarks:', e);
      return null;
    }
  }, [pose?.landmarks]);

  const drawPose = useCallback(
    (landmarks, isAccurate) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (webcamRef.current?.video) {
        canvas.width = webcamRef.current.video.videoWidth;
        canvas.height = webcamRef.current.video.videoHeight;
      }

      const lineColor = isAccurate ? 'limegreen' : 'white';
      ctx.lineWidth = 2;

      const connections = [
        [0, 1],
        [1, 2],
        [2, 3],
        [3, 7],
        [0, 4],
        [4, 5],
        [5, 6],
        [6, 8],
        [9, 10],
        [11, 12],
        [11, 13],
        [13, 15],
        [12, 14],
        [14, 16],
        [11, 23],
        [12, 24],
        [23, 24],
        [23, 25],
        [25, 27],
        [24, 26],
        [26, 28]
      ];

      ctx.strokeStyle = lineColor;
      connections.forEach(([i, j]) => {
        if (landmarks[i] && landmarks[j]) {
          ctx.beginPath();
          ctx.moveTo(landmarks[i].x * canvas.width, landmarks[i].y * canvas.height);
          ctx.lineTo(landmarks[j].x * canvas.width, landmarks[j].y * canvas.height);
          ctx.stroke();
        }
      });

      landmarks.forEach((point, index) => {
        if (point) {
          const x = point.x * canvas.width;
          const y = point.y * canvas.height;
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, Math.PI * 2);
          ctx.fillStyle = 'white';
          ctx.fill();
          ctx.beginPath();
          ctx.arc(x, y, 2, 0, Math.PI * 2);
          ctx.fillStyle = 'black';
          ctx.fill();
          ctx.font = '12px Arial';
          ctx.fillStyle = 'red';
          ctx.fillText(index, x + 5, y);
        }
      });
    },
    [webcamRef, canvasRef]
  );


  useEffect(() => {
  console.log('🧹 Hard reset tracking loop for new pose:', pose?.posturename);

  // STOP animation loop
  if (animationFrameRef.current) {
    cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = null;
  }

  // RESET timing refs
  lastVideoTimeRef.current = -1;
  consecutiveFramesRef.current = 0;
  completedRef.current = false;
  goodPostureStartRef.current = null;
  lastDistanceStateRef.current = null;
  captureAccuracySamplesRef.current = [];
  captureFrameCountRef.current = 0;
  captureStartTimeRef.current = null;
  lastToastRef.current = 0;
  lastCountdownRef.current = 0;

  setAccuracy(0);
  setGoodPostureTime(0);
  setIsCapturing(false);
  setIsTracking(false);
}, [pose?.id]);



  const completeAsanaMutation = useMutation({
    mutationFn: async ({ studentId, courseId, postureId, asanaimage, studentLandmarks }) => {
      return completeAsana({ studentId, courseId, postureId, asanaimage, studentLandmarks });
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries(['asanas', Number(pose.courseId), Number(studentId)]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to mark asana as completed');
    }
  });

  const captureImage = useCallback(async () => {
    const webcam = webcamRef.current;
    console.log('captureImage: Starting capture - Webcam:', !!webcam);

    if (!webcam || !webcam.getScreenshot) {
      console.error('captureImage: Webcam not ready or getScreenshot unavailable', { webcam: !!webcam });
      toast.error('Failed to capture image: Webcam not ready');
      return { imageUrl: null, studentLandmarks: null };
    }

    try {
      // Capture raw image from webcam
      const imageDataUrl = webcam.getScreenshot();
      if (!imageDataUrl) {
        console.error('captureImage: Failed to get screenshot from webcam');
        toast.error('Failed to capture image data');
        return { imageUrl: null, studentLandmarks: null };
      }

      // Detect landmarks for the final frame
      const results = await poseLandmarker.detectForVideo(webcam.video, performance.now());
      const studentLandmarks = results.landmarks?.[0] || null;

      // Convert data URL to blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      if (!blob) {
        console.error('captureImage: Failed to create blob from data URL');
        toast.error('Failed to process captured image');
        return { imageUrl: null, studentLandmarks };
      }

      const arrayBuffer = await blob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const fileKey = `Uploads/${uuidv4()}.jpeg`;
      console.log('captureImage: Preparing S3 upload with fileKey:', fileKey);

      await s3awsConfig.send(
        new PutObjectCommand({
          Bucket: 'roacs-bucket',
          Key: fileKey,
          Body: uint8Array,
          ContentType: 'image/jpeg'
        })
      );

      const imageUrl = `https://roacs-bucket.s3.ap-south-1.amazonaws.com/${fileKey}`;
      console.log('captureImage: Successfully uploaded student image to S3:', imageUrl);
      return { imageUrl, studentLandmarks };
    } catch (error) {
      console.error('captureImage: S3 upload failed:', error);
      toast.error('Failed to upload image to S3: ' + error.message);
      return { imageUrl: null, studentLandmarks: null };
    }
  }, [webcamRef, poseLandmarker, s3awsConfig]);

  const calculateAccuracyWithDistance = useCallback((currentPose, currentInstructorPose, lastDistanceState) => {
    if (!currentPose || !currentInstructorPose || currentPose.length !== currentInstructorPose.length) {
      console.warn('Invalid pose data:', {
        currentPoseLength: currentPose?.length,
        currentInstructorPoseLength: currentInstructorPose?.length
      });
      return { accuracy: 0, distanceState: 'unknown' };
    }

    const mirroredCurrentPose = currentPose.map((point) => ({
      ...point,
      x: 1 - point.x
    }));

    const importantLandmarks = {
      standing: [11, 12, 23, 24, 25, 26, 27, 28],
      sitting: [11, 12, 23, 24, 25, 26],
      arms: [11, 12, 13, 14, 15, 16],
      legs: [23, 24, 25, 26, 27, 28],
      all: Array.from({ length: 33 }, (_, i) => i)
    };

    const calculateAngle = (p1, p2, p3) => {
      if (!p1 || !p2 || !p3) return null;
      const v1 = { x: p1.x - p2.x, y: p1.y - p2.y };
      const v2 = { x: p3.x - p2.x, y: p3.y - p2.y };
      const dot = v1.x * v2.x + v1.y * v2.y;
      const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
      const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
      const cos = dot / (mag1 * mag2);
      return Math.acos(Math.max(-1, Math.min(1, cos))) * (180 / Math.PI);
    };

    const landmarkDiffs = mirroredCurrentPose
      .map((point, index) => {
        if (!point || !currentInstructorPose[index]) return null;
        const dx = point.x - currentInstructorPose[index].x;
        const dy = point.y - currentInstructorPose[index].y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        let weight = 1.0;
        if (importantLandmarks.standing.includes(index)) weight = 1.5;
        if (importantLandmarks.arms.includes(index)) weight = 1.3;
        if (importantLandmarks.legs.includes(index)) weight = 1.3;
        return { distance, weight, index };
      })
      .filter((diff) => diff !== null);

    if (landmarkDiffs.length === 0) {
      console.warn('No valid landmarks for accuracy calculation');
      return { accuracy: 0, distanceState: 'unknown' };
    }

    const angleDiffs = [];
    const backAngleCurrent = calculateAngle(mirroredCurrentPose[11], mirroredCurrentPose[23], mirroredCurrentPose[25]);
    const backAngleInstructor = calculateAngle(currentInstructorPose[11], currentInstructorPose[23], currentInstructorPose[25]);
    if (backAngleCurrent && backAngleInstructor) {
      angleDiffs.push(Math.abs(backAngleCurrent - backAngleInstructor));
    }

    const sideAngleCurrent = calculateAngle(mirroredCurrentPose[11], mirroredCurrentPose[23], mirroredCurrentPose[27]);
    const sideAngleInstructor = calculateAngle(currentInstructorPose[11], currentInstructorPose[23], currentInstructorPose[27]);
    if (sideAngleCurrent && sideAngleInstructor) {
      angleDiffs.push(Math.abs(sideAngleCurrent - sideAngleInstructor));
    }

    const totalWeight = landmarkDiffs.reduce((sum, diff) => sum + diff.weight, 0);
    const weightedDiff = landmarkDiffs.reduce((sum, diff) => sum + diff.distance * diff.weight, 0);
    const avgDiff = weightedDiff / totalWeight;

    const angleAccuracy = angleDiffs.length > 0 ? 100 - angleDiffs.reduce((sum, diff) => sum + diff, 0) / angleDiffs.length / 2 : 100;

    const positionAccuracy = Math.max(0, 100 - (avgDiff / 0.35) * 100);
    const accuracy = positionAccuracy * 0.6 + angleAccuracy * 0.4;

    const significantDeviation = landmarkDiffs.some((diff) => {
      if (importantLandmarks.standing.includes(diff.index)) {
        return diff.distance > 0.4;
      }
      return diff.distance > 0.5;
    });

    const isPoseCorrect = accuracy > 81 && !significantDeviation;

    const thresholds = {
      too_close: 0.07,
      okay_low: 0.1,
      okay_high: 0.3,
      too_far: 0.35
    };
    let distanceState = lastDistanceStateRef.current || 'unknown';

    if (distanceState === 'too_far' && avgDiff < thresholds.okay_high) {
      distanceState = 'okay';
    } else if (distanceState === 'okay') {
      if (avgDiff > thresholds.too_far) distanceState = 'too_far';
      else if (avgDiff < thresholds.too_close) distanceState = 'too_close';
    } else if (distanceState === 'too_close' && avgDiff > thresholds.okay_low) {
      distanceState = 'okay';
    } else if (distanceState === 'unknown') {
      if (avgDiff > thresholds.too_far) distanceState = 'too_far';
      else if (avgDiff < thresholds.too_close) distanceState = 'too_close';
      else distanceState = 'okay';
    }

    return { accuracy: isPoseCorrect ? accuracy : 0, distanceState, isPoseCorrect };
}, [currentInstructorPose]);

  const handleDistanceFeedback = useCallback(
    (state) => {
      if (lastDistanceStateRef.current === state) return;

      const significantChange =
        (lastDistanceStateRef.current === 'too_far' && state !== 'okay') ||
        (lastDistanceStateRef.current === 'too_close' && state !== 'okay') ||
        (lastDistanceStateRef.current === 'okay' && (state === 'too_far' || state === 'too_close'));

      if (!significantChange && lastDistanceStateRef.current !== null) return;

      lastDistanceStateRef.current = state;

      let message = '';
      if (state === 'too_far') message = 'Step back a little from the camera.';
      else if (state === 'too_close') message = 'Move closer to the camera.';
      else if (state === 'okay' && lastDistanceStateRef.current !== null) {
        message = 'Perfect distance. Start your position.';
      }

      if (message) {
        console.debug('Distance feedback:', message);
        speakCountdown(message);
      }
    },
    [speakCountdown]
  );

  useEffect(() => {
    if (!isTracking || loading || !poseLandmarker) return;

    const detectAndTrack = async () => {
      if (!webcamRef.current?.video || !canvasRef.current || webcamRef.current.video.readyState < 3) {
        animationFrameRef.current = requestAnimationFrame(detectAndTrack);
        return;
      }

      const now = performance.now();
      if (now - lastVideoTimeRef.current < 66) {
        animationFrameRef.current = requestAnimationFrame(detectAndTrack);
        return;
      }
      lastVideoTimeRef.current = now;

      const t0 = performance.now();
      try {
        const results = await poseLandmarker.detectForVideo(webcamRef.current.video, t0);
        const t1 = performance.now();
        setLatencyMs((t1 - t0).toFixed(1));
        setFps((1000 / (t1 - t0)).toFixed(1));

        if (results.landmarks?.length > 0) {
          const currentPose = results.landmarks[0];
          const { accuracy, distanceState, isPoseCorrect } = calculateAccuracyWithDistance(
            currentPose,
            currentInstructorPose,
            lastDistanceStateRef.current
          );

          setAccuracy(accuracy);
          handleDistanceFeedback(distanceState);

          if (!isPoseCorrect && now - lastToastRef.current > 2000) {
            speakCountdown('Adjust your pose to match the instructor. Try to align your body position.');
            lastToastRef.current = now;
          }

          if (isPoseCorrect) {
            consecutiveFramesRef.current += 1;
            if (consecutiveFramesRef.current >= REQUIRED_CONSECUTIVE_FRAMES && !isCapturing) {
              setIsCapturing(true);
              captureStartTimeRef.current = now;
              speakCountdown('Pose capturing started! Hold your position.');
              toast.success('Pose capturing started!');
            }

            if (isCapturing) {
              if (!goodPostureStartRef.current) {
                goodPostureStartRef.current = now;
              }
              const elapsed = (now - goodPostureStartRef.current) / 1000;
              setGoodPostureTime(elapsed.toFixed(1));
              captureFrameCountRef.current += 1;
              captureAccuracySamplesRef.current.push(accuracy);

              const remainingTime = Math.ceil(HOLD_DURATION_SEC - elapsed);
              if (remainingTime > 0 && remainingTime <= 12 && now - lastCountdownRef.current > 1000) {
                speakCountdown(remainingTime.toString());
                lastCountdownRef.current = now;
              }

              if (elapsed >= HOLD_DURATION_SEC && !completedRef.current) {
                completedRef.current = true;
                const captureDuration = ((now - captureStartTimeRef.current) / 1000).toFixed(1);
                const averageAccuracy = captureAccuracySamplesRef.current.length
                  ? (
                      captureAccuracySamplesRef.current.reduce((sum, acc) => sum + acc, 0) / captureAccuracySamplesRef.current.length
                    ).toFixed(1)
                  : 0;

                // Capture the student's image with landmarks
                console.log('Capturing student image at completion...');
                const { imageUrl, studentLandmarks } = await captureImage();
                console.log('Captured asanaimage after completion:', imageUrl, 'studentLandmarks:', studentLandmarks);

                if (!imageUrl) {
                  console.error('Failed to capture student image, falling back to coach image');
                  toast.error('Failed to capture student image, using coach image');
                }

                const sessionData = {
                  captureDuration,
                  averageAccuracy,
                  frameCount: captureFrameCountRef.current,
                  goodPostureTime: elapsed.toFixed(1),
                  studentId: Number(studentId),
                  courseId: Number(pose.courseId),
                  poseId: Number(pose.id),
                  posturename: pose.posturename,
                  asanaimage: imageUrl || pose.imasanaImageage,
                  studentLandmarks: JSON.stringify(studentLandmarks) // Add student landmarks
                };
                setSessionData(sessionData);
                setIsTracking(false);

                // Call completeAsana mutation with image and landmarks
                console.log('completeAsanaMutation payload:', {
                  studentId: Number(studentId),
                  courseId: Number(pose.courseId),
                  postureId: Number(pose.id),
                  asanaimage: imageUrl || pose.asanaImage,
                  studentLandmarks: JSON.stringify(studentLandmarks)
                });
                completeAsanaMutation.mutate({
                  studentId: Number(studentId),
                  courseId: Number(pose.courseId),
                  postureId: Number(pose.id),
                  asanaimage: imageUrl || pose.asanaImage,
                  studentLandmarks: JSON.stringify(studentLandmarks)
                });

                if (canvasRef.current) {
                  const ctx = canvasRef.current.getContext('2d');
                  ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                }

                if (typeof window !== 'undefined' && 'speechSynthesis' in window && typeof window.speechSynthesis.cancel === 'function') {
                  window.speechSynthesis.cancel();
                }

                const successMessage = new SpeechSynthesisUtterance('Excellent! You have successfully completed the pose. Great job!');
                successMessage.lang = 'en-US';
                successMessage.rate = 1.0;
                successMessage.onend = () => {
                  const route =
                    pose.sequenceType === 'standalone' ? `/asanas/${pose.courseId}/standalone` : `/asanas/${pose.courseId}/continuous`;
                  console.log('Navigating with:', { route, sessionData, id: pose.id, posturename: pose.posturename });
                  navigate(route, {
                    state: { completed: true, sessionData, id: pose.id, posturename: pose.posturename }
                  });
                };
                window.speechSynthesis.speak(successMessage);

                toast.success(`Good job! Held for ${elapsed.toFixed(1)} seconds 🎉`);
                speakCountdown(successMessage);

                // Wait 2 seconds, then move to next pose WITHOUT navigation
                setTimeout(() => {
  if (onNextPose) {
    onNextPose();
  }
}, 3500);

              }
            }

            drawPose(currentPose, isPoseCorrect);
          } else {
            consecutiveFramesRef.current = 0;
            goodPostureStartRef.current = null;
            setGoodPostureTime(0);
            drawPose(currentPose, false);
          }
        } else {
          console.warn('No landmarks detected in frame');
          setAccuracy(0);
          setGoodPostureTime(0);
          goodPostureStartRef.current = null;
          consecutiveFramesRef.current = 0;
          if (isCapturing) captureAccuracySamplesRef.current.push(0);
          if (now - lastToastRef.current > 2000) {
            speakCountdown('Your body is not visible in the frame. Please step into view.');
            toast.error('Your body is not visible in the frame.');
            lastToastRef.current = now;
          }
        }
      } catch (error) {
        console.error('Detection error:', error);
        toast.error('Pose detection failed. Please ensure you are visible in the camera.');
        speakCountdown('Unable to detect your pose. Please make sure you are visible in the camera.');
      }

      animationFrameRef.current = requestAnimationFrame(detectAndTrack);
    };

    animationFrameRef.current = requestAnimationFrame(detectAndTrack);
    return () => cancelAnimationFrame(animationFrameRef.current);
  }, [
    isTracking,
    loading,
    poseLandmarker,
    currentInstructorPose,
    navigate,
    calculateAccuracyWithDistance,
    handleDistanceFeedback,
    drawPose,
    speakCountdown,
    isCapturing,
    setIsTracking,
    setSessionData,
    pose,
    completeAsanaMutation,
    studentId,
    queryClient,
    captureImage
  ]);

  return { accuracy, goodPostureTime, latencyMs, fps };



  
};

import { Fullscreen, FullscreenExit } from '@mui/icons-material';
import { IconButton } from '@mui/material';

const PoseTracking = ({ pose, currentPoseIndex, totalPoses, isFlowSequence, titleText }) => {
  // Custom hook for countdown
  const useCountdown = (speakCountdown, setIsTracking, poseId) => {
    const [countdown, setCountdown] = useState(10);
    const intervalRef = useRef(null);

    useEffect(() => {
      // Reset countdown display
      setCountdown(10);

      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Cancel any ongoing speech
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }

      // Announce readiness
      speakCountdown('Get ready! Training will start in 10 seconds');

      // Start new countdown
      intervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          const next = prev - 1;

          if (next === 5) speakCountdown('5 seconds remaining');
          if (next <= 3 && next > 0) speakCountdown(next.toString());
          if (next === 0) {
            speakCountdown('Go! Hold your pose.');
          }

          if (next <= 0) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            setIsTracking(true);
            return 'Done';
          }

          return next;
        });
      }, 1000);

      // Cleanup on unmount or pose change
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
          window.speechSynthesis.cancel();
        }
      };
    }, [poseId, speakCountdown, setIsTracking]); // ← Critical: depend on poseId

    return countdown;
  };

  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const { poseLandmarker, loading } = usePoseLandmarker(pose);
  const [isTracking, setIsTracking] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const speakCountdown = useSpeechSynthesis();
  const countdown = useCountdown(speakCountdown, setIsTracking, pose?.id);
  const [sessionData, setSessionData] = useState({
    /* ... */
  });

  const { accuracy, goodPostureTime, latencyMs, fps } = usePoseTracking(
    isTracking,
    loading,
    poseLandmarker,
    webcamRef,
    canvasRef,
    navigate,
    speakCountdown,
    setIsTracking,
    setSessionData,
    pose
  );

  const { isPortrait } = useOrientation();

  useEffect(() => {
  if (!isFlowSequence || !containerRef.current) return;

  if (!document.fullscreenElement) {
    containerRef.current.requestFullscreen()
      .then(() => setIsFullscreen(true))
      .catch(() => {});
  }
}, [pose?.id, isFlowSequence]);


  // Block Esc key
  useEffect(() => {
    if (!isFlowSequence) return;

    const blockEsc = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (containerRef.current && !document.fullscreenElement) {
          containerRef.current.requestFullscreen().catch(() => {});
        }
      }
    };
    document.addEventListener('keydown', blockEsc);
    return () => document.removeEventListener('keydown', blockEsc);
  }, [isFlowSequence]);

  // Manual toggle
  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      await containerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Prevent multiple triggers
  const hasTriggeredNext = useRef(false);
  useEffect(() => {
    hasTriggeredNext.current = false;
  }, [pose?.id]);

  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h5">Loading Pose Detection Model...</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        p: 2,
        backgroundColor: '#000',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        position: 'relative'
      }}
    >
      <OrientationWarningOverlay />

      {/* Optional: Hide button for full immersion */}
      {(!isFlowSequence || true) && ( // change to false to hide completely
        <IconButton
          onClick={toggleFullscreen}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: '#fff',
            backgroundColor: 'rgba(0,0,0,0.5)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
            zIndex: 10
          }}
        >
          {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
        </IconButton>
      )}

      <Typography variant="h4" gutterBottom textAlign="center" sx={{ fontWeight: 'bold', mb: 2, color: '#fff' }}>
        Pose {currentPoseIndex + 1} of {totalPoses}: {pose?.posturename}
      </Typography>

      {/* Your beautiful UI exactly as before */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: isPortrait ? 'column' : 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3
        }}
      >
        <Box
          sx={{
            flex: 1,
            maxWidth: isPortrait ? '100%' : '50%',
            height: isPortrait ? '50%' : '100%',
            position: 'relative',
            boxShadow: '0 0 8px rgba(0,0,0,0.15)',
            border: accuracy > 95 ? '4px solid limegreen' : '4px solid transparent',
            borderRadius: 2
          }}
        >
          {isTracking ? (
            <>
              <Webcam
                ref={webcamRef}
                videoConstraints={{ facingMode: 'user' }}
                style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
              />
              <canvas
                ref={canvasRef}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', transform: 'scaleX(-1)' }}
              />
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', backgroundColor: '#121212' }}>
              <Typography variant="h1" sx={{ fontSize: '10rem', fontWeight: 700, color: '#fff' }}>
                {countdown}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            maxWidth: isPortrait ? '100%' : '50%',
            height: isPortrait ? '50%' : '100%',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: 2
          }}
        >
          <img src={pose?.asanaImage} alt={`Pose: ${pose?.posturename}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>
      </Box>

      {isTracking && (
        <>
          <LinearProgress
            variant="determinate"
            value={accuracy}
            sx={{
              mt: 3,
              height: 12,
              borderRadius: 5,
              backgroundColor: '#333',
              '& .MuiLinearProgress-bar': {
                borderRadius: 5,
                backgroundColor: accuracy > 95 ? '#4caf50' : '#f44336'
              }
            }}
          />

          {accuracy > 81 && (
            <>
              <Typography variant="h6" align="center" sx={{ mt: 2, color: '#fff' }}>
                Accuracy: {Math.round(accuracy)}% | Latency: {latencyMs}ms | FPS: {fps}
              </Typography>
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Typography variant="body1" sx={{ color: '#4caf50', fontWeight: 600, fontSize: '1.2rem' }}>
                  Good Posture Time: {goodPostureTime}s
                </Typography>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default PoseTracking;
